import AXIOS_INSTANCE from '@/src/api/axiosInstance'
import { useImagingOrder, usePatient } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { imagingOrderFormSchema, type ImagingOrderFormValues } from '@schema/imagingOrder.schema'
import { GeneralResMock } from '@type/general.type'
import { ImagingOrderCreatePayload } from '@type/imaging_order.type'
import { LaboratoryResMock } from '@type/laboratory.type'
import { FormButton, Modal, SelectInputField, TextAreaField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import { debounce } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useParams, useSearchParams } from 'react-router-dom'
import type { GroupBase } from 'react-select'

type Props = {
  onClose: () => void
  patientId?: string
  encounterId?: string
}

/** Catalog options carry a numeric id; created (free-text) options carry the typed string. */
type ImagingStudyOption = { label: string; value: number | string }

type ExtendedImagingOrderFormValues = ImagingOrderFormValues & { patient_id?: string }

async function searchImagings(inputValue: string): Promise<ImagingStudyOption[]> {
  const q = new URLSearchParams({
    'page[number]': '1',
    'page[size]': '50',
    sort_by: 'study_type',
    sort_dir: 'asc'
  })
  const trimmed = inputValue.trim()
  if (trimmed) q.set('query', trimmed)

  const { data } = await AXIOS_INSTANCE.get<GeneralResMock<LaboratoryResMock[]>>(`/imagings?${q.toString()}`)
  const rows = Array.isArray(data.data) ? data.data : []
  return rows.map(imaging => ({
    label: imaging.attributes.study_type || imaging.attributes.name,
    value: Number(imaging.id)
  }))
}

function buildImagingOrderPayload(
  patientId: string,
  imaging_ids: number[],
  note: string,
  encounterId?: string,
  study_names: string[] = []
): ImagingOrderCreatePayload {
  const relationships: ImagingOrderCreatePayload['data']['relationships'] = {
    patient: { data: { id: patientId, type: 'patient' } }
  }
  // Walk-in (no encounter): omit the relationship — the backend creates a draft invoice.
  if (encounterId) {
    relationships.encounter = { data: { id: encounterId, type: 'encounter' } }
  }

  return {
    data: {
      type: 'imaging_order',
      attributes: {
        imaging_ids,
        note: note.trim(),
        ...(study_names.length > 0 ? { study_names } : {})
      },
      relationships
    }
  }
}

const AddImagingOrder = ({ onClose, patientId: patientIdProp, encounterId: encounterIdProp }: Props) => {
  const { patientId: patientIdParam, encounterId: encounterIdParam } = useParams<{
    patientId: string
    encounterId: string
  }>()
  const resolvedPatientId = patientIdProp ?? patientIdParam
  const resolvedEncounterId = encounterIdProp ?? encounterIdParam
  const [, setSearchParams] = useSearchParams()
  const methods = useForm<ExtendedImagingOrderFormValues>({
    defaultValues: { imaging_studies: [], note: '' },
    resolver: yupResolver(imagingOrderFormSchema)
  })
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting }
  } = methods

  // Mutations only — do not mount an unfiltered list query that races the page list.
  const { createImagingOrder } = useImagingOrder()

  const { singlePatient } = usePatient({ id: resolvedPatientId })

  const [patientSearch, setPatientSearch] = useState('')
  const debouncedSetPatientSearch = useCallback(
    debounce((q: string) => setPatientSearch(q), 400),
    []
  )
  const handlePatientSearch = (q: string) => debouncedSetPatientSearch(q)

  const { patients } = usePatient({ query: patientSearch, page: 1, limit: 50 })

  // If the modal was opened with an existing patient, populate the default value.
  useEffect(() => {
    if (singlePatient) {
      setValue('patient_id', String(singlePatient.id))
    } else if (resolvedPatientId) {
      setValue('patient_id', String(resolvedPatientId))
    }
  }, [singlePatient, resolvedPatientId, setValue])

  const loadImagingOptions = useCallback((inputValue: string) => searchImagings(inputValue), [])

  const onSubmit = async (form: ExtendedImagingOrderFormValues) => {
    const finalPatientId = (watch('patient_id') as string | undefined) ?? resolvedPatientId
    if (!finalPatientId) {
      showToast('Missing patient')
      return
    }

    const imaging_ids = form.imaging_studies.filter(s => typeof s.value === 'number').map(s => s.value as number)
    const study_names = form.imaging_studies.filter(s => typeof s.value === 'string').map(s => String(s.label).trim())
    const payload = buildImagingOrderPayload(
      finalPatientId,
      imaging_ids,
      form.note ?? '',
      resolvedEncounterId,
      study_names
    )

    try {
      await createImagingOrder(payload)
      // Radiology list is newest-first on page 1; clear filters that would hide the new row.
      if (!resolvedEncounterId) {
        setSearchParams(prev => {
          prev.delete('query')
          prev.delete('captured_date')
          prev.set('status', 'all')
          prev.set('page', '1')
          return prev
        })
      }
      showToast('Imaging order placed successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Order imaging">
        <form className="flex min-w-[530px] flex-col gap-4 overflow-auto" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-4 overflow-auto p-6">
            {!resolvedPatientId && (
              <SelectInputField
                dropProps={{ align: { left: 'left', right: 'right' }, height: '200px' }}
                label="Search patient"
                labelKey={({ attributes }) => `${attributes.first_name} ${attributes.last_name}`}
                name="patient_id"
                onSearch={handlePatientSearch}
                options={patients || []}
                placeholder="Search Patient"
                searchPlaceholder="eg John doe"
                valueKey={{ key: 'id', reduce: true }}
              />
            )}

            <Controller
              control={control}
              name="imaging_studies"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select<ImagingStudyOption, true, GroupBase<ImagingStudyOption>>
                  borderRadius={'15px'}
                  defaultOptions
                  error={error?.message}
                  getNewOptionData={(inputValue, optionLabel) => ({
                    label: String(optionLabel),
                    value: String(inputValue).trim()
                  })}
                  getOptionLabel={o => o.label}
                  getOptionValue={o => String(o.value)}
                  isAsync
                  isCreatable
                  isMulti
                  label="Study"
                  loadOptions={loadImagingOptions}
                  onChange={opts => onChange((opts as ImagingStudyOption[] | null) ?? [])}
                  placeholder="Search catalog or type a study name"
                  value={value as ImagingStudyOption[]}
                />
              )}
            />

            <TextAreaField label="Note to radiology" name="note" placeholder="Enter Note" />
          </div>
          <FormButton isLoading={isSubmitting} onClose={onClose} submit="Order" />
        </form>
      </Modal>
    </FormProvider>
  )
}

export default AddImagingOrder
